import React, { useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'antd';
import V2Form from '@/common/components/Form/V2Form';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { getTemplateLists } from '@/common/api/expandStore/chancepoint';
import { useMethods } from '@lhb/hook';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

/**
 * 选择模版弹框
 * @Description 拓店任务-开店计划-详情-创建机会点
 */
const SelectTemplateModal: React.FC<{
  visible,
  chancepointAlias,
  setVisible,
  setFormDrawerData?, // 选中后弹出新建机会点表单弹框（一般场景）
  onSelectTemplate?, // 选中模板的回调（自定义处理场景）
}> = ({
  visible,
  chancepointAlias,
  setVisible,
  setFormDrawerData,
  onSelectTemplate,
}) => {

  /** 真正控制弹窗显示 */
  const [curVisible, setCurVisible] = useState(false);

  const [form] = Form.useForm();
  const [options, setOptions] = useState<any>([]);
  const requestRef = useRef(0);

  useEffect(() => {
    if (visible) {
      methods.loadOptions();
    } else {
      setCurVisible(false);
    }
    return () => { requestRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const { onCancel, onSubmit, ...methods } = useMethods({
    onCancel() {
      setVisible(false);
    },

    onSubmit() {
      form.validateFields().then((values: any) => {
        const templateId = values?.templateId;
        onSelectTemplate?.({ templateId });
        setFormDrawerData?.({
          templateId,
          open: true,
        });
        onCancel();
      });
    },

    async loadOptions() {
      const requestId = ++requestRef.current;
      try {
        const result: any = await getTemplateLists();
        if (requestId !== requestRef.current) return;
        if (!Array.isArray(result) || !result.length) {
          setOptions([]);
          V2Message.warning(`暂无可用的${chancepointAlias || '机会点'}模板`);
          setVisible(false);
          return;
        }
        const nextOptions = result.map(item => ({ label: item.templateName, value: item.id }));
        setOptions(nextOptions);
        // 使用本次返回值，避免状态更新尚未提交时读到上一次模板列表。
        if (nextOptions.length === 1) {
          const templateId = nextOptions[0].value;
          onSelectTemplate?.({ templateId });
          setFormDrawerData?.({ templateId, open: true });
          onCancel();
        } else {
          form.resetFields();
          setCurVisible(true);
        }
      } catch {
        if (requestId === requestRef.current) setVisible(false);
      }
    },

    init() {
      if (!Array.isArray(options)) {
        return;
      }
      // 如果只有一条数据，直接选中，否则展示弹窗
      if (options.length === 1) {
        methods.handleAutoSelectSingleItem();
      } else if (options.length > 1) {
        setCurVisible(true);
      }
    },

    /** 自动选择单条数据 */
    handleAutoSelectSingleItem() {
      if (Array.isArray(options) && options.length === 1) {
        const templateId = options[0].value;
        onSelectTemplate?.({ templateId });
        setFormDrawerData?.({ templateId, open: true, });
        onCancel();
      }
    },

  });

  return (
    <Modal title={`选择${chancepointAlias}模板`} open={curVisible} width={336} onOk={onSubmit} onCancel={onCancel}>
      <V2Form form={form}>
        <V2FormSelect
          label='选择模板'
          name='templateId'
          placeholder='请选择模板'
          rules={[{ required: true, message: '请选择模板' }]}
          options={options}
        />
      </V2Form>
    </Modal>
  );
};

export default SelectTemplateModal;
